import { PLURAL_SUFFIX } from "@/i18n/plural";

export interface DashboardTranslationEntry {
  message: string;
  description: string;
}

const descriptions = {
  accountsDescription: "Description for the dashboard account-balance card",
  allWidgetsHidden: "Empty state when every dashboard module is hidden",
  chartView: "Accessible label for switching a financial card to chart view",
  costBasis:
    "The net worth valued at cost, shown beside its market value. {amount} is an amount with its currency",
  customize: "Button and panel title for dashboard customization",
  customizeDescription: "Description for dashboard customization controls",
  financialPosition: "Heading for net worth and account balances",
  financialPositionDescription:
    "Description for the financial position section",
  fromPreviousMonth: "Label for a change compared with the previous month",
  ledgerNotes: "Label for the ledger README dashboard module",
  moneyIn: "Title for monthly income flow",
  moneyMovement: "Heading for monthly income and spending",
  moneyMovementDescription: "Description for the money movement section",
  moneyOut: "Title for monthly expense flow",
  moveDown: "Accessible label for moving a dashboard module down",
  moveUp: "Accessible label for moving a dashboard module up",
  netWorthDescription: "Description for the net-worth trend card",
  noMovement: "Empty state for a month without income or expenses",
  noRecentActivity: "Empty state for recent transactions",
  partialPeriod: "Short label indicating that a month is incomplete",
  precedingPeriodsAverage:
    "Label for the average over up to three preceding periods in the selection",
  recentActivity: "Heading for recent journal transactions",
  recentActivityCardDescription:
    "Description inside the recent transaction card",
  recentActivityDescription: "Description for the recent activity section",
  recentActivityError: "Error state for recent transactions",
  resetLayout: "Button to restore the default dashboard layout",
  showWidget: "Accessible label for showing a dashboard module",
  tableView: "Accessible label for switching a financial card to table view",
  topSources: "Heading for the largest income accounts",
  topSpending: "Heading for the largest expense accounts",
  transactionCleared: "Status label for a cleared transaction",
  transactionMultiple:
    "Label for a transaction without one representative amount",
  transactionOther: "Status label for another transaction flag",
  transactionPending: "Status label for a pending transaction",
  transactionTransfer: "Label for a transfer between balance-sheet accounts",
  unknownTransaction:
    "Fallback title for a transaction without payee or narration",
  unrealized:
    "Market value minus cost basis of the net worth, signed. {amount} is an amount with its currency",
  atCostCount:
    "Status count of holdings valued at cost because they have no price",
  holdingPrice: "Latest price date of one holding in the valuation details",
  livePrice:
    "Tag for a holding priced by a managed Beancount.io live price feed",
  noPrice: "Shown for a holding that has no price",
  notInTotalCount: "Status count of holdings left out of the net worth total",
  notInTotalTag: "Tag for a holding left out of the net worth total",
  atCostTag: "Tag for a holding valued at cost for lack of a price",
  priceNotUpdated:
    "Marker for a holding whose price has not been updated for longer than usual",
  pricesNotUpdated:
    "Status count of prices not updated for longer than usual, with the oldest date",
  updatePrices: "Link from the valuation details to the Commodities page",
  valuationDetails:
    "Title and accessible label of the net worth valuation details",
  valuedAtMarket:
    "Basis label for a net worth whose holdings are valued at market prices",
  viewAll: "Link label to open the complete related report",
  viewAllAccounts: "Accessible link label to open the account list",
} as const;

type DashboardTranslationKey = keyof typeof descriptions;
type DashboardLanguage =
  | "bg"
  | "ca"
  | "de"
  | "en"
  | "es"
  | "fa"
  | "fr"
  | "ja"
  | "ko"
  | "nl"
  | "pt"
  | "ru"
  | "sk"
  | "uk"
  | "zh";

/** Keys whose message is a count, written once per plural category. */
type PluralKey = "atCostCount" | "notInTotalCount" | "pricesNotUpdated";
type PluralCategory = "one" | "two" | "few" | "many";

/**
 * Plural keys are flat `key_<category>` entries, as i18next resolves them from
 * `{ count }`, carrying exactly the categories each language uses
 * (`Intl.PluralRules`); `src/i18n/__tests__/plural.test.ts` fails on a missing
 * or extra one. Every other key keeps strict parity across languages.
 */
type DashboardMessages = {
  [K in Exclude<DashboardTranslationKey, PluralKey>]: string;
} & { [K in PluralKey as `${K}_other`]: string } & {
  [K in PluralKey as `${K}_${PluralCategory}`]?: string;
};

const messages: Record<DashboardLanguage, DashboardMessages> = {
  en: {
    accountsDescription: "Current asset and liability account balances",
    allWidgetsHidden:
      "All dashboard modules are hidden. Customize the dashboard to restore one.",
    chartView: "Show chart",
    costBasis: "Cost basis {amount}",
    customize: "Customize",
    customizeDescription:
      "Choose which dashboard modules appear and arrange them for this ledger.",
    financialPosition: "Financial position",
    financialPositionDescription: "Net worth and the accounts that make it up",
    fromPreviousMonth: "from previous month",
    ledgerNotes: "Ledger notes",
    moneyIn: "Money in",
    moneyMovement: "Money movement",
    moneyMovementDescription: "Monthly income and spending by account category",
    moneyOut: "Money out",
    moveDown: "Move down",
    moveUp: "Move up",
    netWorthDescription: "Assets plus liabilities over the last 12 months",
    noMovement: "No money movement for this period.",
    noRecentActivity: "No recent transactions match the current filters.",
    partialPeriod: "partial",
    precedingPeriodsAverage: "Average of up to 3 preceding periods",
    recentActivity: "Recent activity",
    recentActivityCardDescription:
      "Newest transactions matching the current dashboard filters",
    recentActivityDescription: "Posting-aware activity from the journal",
    recentActivityError: "Recent transactions could not be loaded.",
    resetLayout: "Reset layout",
    showWidget: "Show module",
    tableView: "Show table",
    topSources: "Top sources",
    topSpending: "Top spending",
    transactionCleared: "Cleared",
    transactionMultiple: "Multiple postings",
    transactionOther: "Other",
    transactionPending: "Pending",
    transactionTransfer: "Transfer",
    unknownTransaction: "Untitled transaction",
    unrealized: "Unrealized {amount}",
    atCostCount_one: "{count} at cost (no price)",
    atCostCount_other: "{count} at cost (no price)",
    holdingPrice: "Price {date}",
    livePrice: "Live price",
    noPrice: "No price",
    notInTotalCount_one: "{count} not in total",
    notInTotalCount_other: "{count} not in total",
    notInTotalTag: "Not in total",
    atCostTag: "At cost",
    priceNotUpdated: "Not updated",
    pricesNotUpdated_one: "{count} price not updated since {date}",
    pricesNotUpdated_other: "{count} prices not updated since {date}",
    updatePrices: "Update prices",
    valuationDetails: "Valuation details",
    valuedAtMarket: "At market value",
    viewAll: "View all",
    viewAllAccounts: "View all accounts",
  },
  bg: {
    accountsDescription: "Текущи салда по сметки за активи и пасиви",
    allWidgetsHidden:
      "Всички модули на таблото са скрити. Персонализирайте таблото, за да възстановите модул.",
    chartView: "Покажи диаграма",
    costBasis: "Цена на придобиване {amount}",
    customize: "Персонализиране",
    customizeDescription:
      "Изберете кои модули да се показват и ги подредете за тази книга.",
    financialPosition: "Финансова позиция",
    financialPositionDescription: "Нетна стойност и сметките, които я формират",
    fromPreviousMonth: "спрямо предходния месец",
    ledgerNotes: "Бележки към книгата",
    moneyIn: "Входящи средства",
    moneyMovement: "Движение на средства",
    moneyMovementDescription: "Месечни приходи и разходи по категория сметки",
    moneyOut: "Изходящи средства",
    moveDown: "Премести надолу",
    moveUp: "Премести нагоре",
    netWorthDescription: "Активи плюс пасиви през последните 12 месеца",
    noMovement: "Няма движение на средства за този период.",
    noRecentActivity: "Няма скорошни транзакции за текущите филтри.",
    partialPeriod: "непълен",
    precedingPeriodsAverage: "Средно за до 3 предходни периода",
    recentActivity: "Скорошна активност",
    recentActivityCardDescription:
      "Най-новите транзакции според текущите филтри",
    recentActivityDescription:
      "Активност от журнала с отчитане на осчетоводяванията",
    recentActivityError: "Скорошните транзакции не можаха да бъдат заредени.",
    resetLayout: "Нулирай подредбата",
    showWidget: "Покажи модул",
    tableView: "Покажи таблица",
    topSources: "Водещи източници",
    topSpending: "Най-големи разходи",
    transactionCleared: "Приключена",
    transactionMultiple: "Няколко осчетоводявания",
    transactionOther: "Друго",
    transactionPending: "Предстояща",
    transactionTransfer: "Прехвърляне",
    unknownTransaction: "Транзакция без заглавие",
    unrealized: "Нереализирана разлика {amount}",
    atCostCount_one: "{count} по цена на придобиване (без цена)",
    atCostCount_other: "{count} по цена на придобиване (без цена)",
    holdingPrice: "Цена {date}",
    livePrice: "Цена на живо",
    noPrice: "Няма цена",
    notInTotalCount_one: "{count} извън общата сума",
    notInTotalCount_other: "{count} извън общата сума",
    notInTotalTag: "Не е в общата сума",
    atCostTag: "По цена на придобиване",
    priceNotUpdated: "Не е обновена",
    pricesNotUpdated_one: "{count} цена не е обновявана от {date}",
    pricesNotUpdated_other: "{count} цени не са обновявани от {date}",
    updatePrices: "Обнови цените",
    valuationDetails: "Подробности за оценката",
    valuedAtMarket: "По пазарна стойност",
    viewAll: "Виж всички",
    viewAllAccounts: "Виж всички сметки",
  },
  ca: {
    accountsDescription: "Saldos actuals dels comptes d'actiu i passiu",
    allWidgetsHidden:
      "Tots els mòduls del tauler estan ocults. Personalitzeu el tauler per restaurar-ne un.",
    chartView: "Mostra el gràfic",
    costBasis: "Cost d'adquisició {amount}",
    customize: "Personalitza",
    customizeDescription:
      "Trieu quins mòduls es mostren i ordeneu-los per a aquest llibre.",
    financialPosition: "Posició financera",
    financialPositionDescription: "Patrimoni net i comptes que el formen",
    fromPreviousMonth: "respecte del mes anterior",
    ledgerNotes: "Notes del llibre",
    moneyIn: "Entrades",
    moneyMovement: "Moviment de diners",
    moneyMovementDescription:
      "Ingressos i despeses mensuals per categoria de compte",
    moneyOut: "Sortides",
    moveDown: "Mou avall",
    moveUp: "Mou amunt",
    netWorthDescription: "Actius més passius dels darrers 12 mesos",
    noMovement: "No hi ha moviment de diners en aquest període.",
    noRecentActivity:
      "Cap transacció recent coincideix amb els filtres actuals.",
    partialPeriod: "parcial",
    precedingPeriodsAverage: "Mitjana de fins a 3 períodes anteriors",
    recentActivity: "Activitat recent",
    recentActivityCardDescription:
      "Transaccions més recents segons els filtres actuals",
    recentActivityDescription:
      "Activitat del diari conscient dels assentaments",
    recentActivityError: "No s'han pogut carregar les transaccions recents.",
    resetLayout: "Restableix la disposició",
    showWidget: "Mostra el mòdul",
    tableView: "Mostra la taula",
    topSources: "Fonts principals",
    topSpending: "Despeses principals",
    transactionCleared: "Liquidada",
    transactionMultiple: "Diversos assentaments",
    transactionOther: "Altres",
    transactionPending: "Pendent",
    transactionTransfer: "Transferència",
    unknownTransaction: "Transacció sense títol",
    unrealized: "Resultat no realitzat {amount}",
    atCostCount_one: "{count} al cost (sense preu)",
    atCostCount_many: "{count} al cost (sense preu)",
    atCostCount_other: "{count} al cost (sense preu)",
    holdingPrice: "Preu {date}",
    livePrice: "Preu en directe",
    noPrice: "Sense preu",
    notInTotalCount_one: "{count} fora del total",
    notInTotalCount_many: "{count} fora del total",
    notInTotalCount_other: "{count} fora del total",
    notInTotalTag: "Fora del total",
    atCostTag: "A cost",
    priceNotUpdated: "No actualitzat",
    pricesNotUpdated_one: "{count} preu sense actualitzar des del {date}",
    pricesNotUpdated_many: "{count} preus sense actualitzar des del {date}",
    pricesNotUpdated_other: "{count} preus sense actualitzar des del {date}",
    updatePrices: "Actualitza els preus",
    valuationDetails: "Detalls de la valoració",
    valuedAtMarket: "A valor de mercat",
    viewAll: "Mostra-ho tot",
    viewAllAccounts: "Mostra tots els comptes",
  },
  de: {
    accountsDescription: "Aktuelle Salden der Aktiv- und Passivkonten",
    allWidgetsHidden:
      "Alle Dashboard-Module sind ausgeblendet. Passen Sie das Dashboard an, um eines wiederherzustellen.",
    chartView: "Diagramm anzeigen",
    costBasis: "Anschaffungskosten {amount}",
    customize: "Anpassen",
    customizeDescription:
      "Wählen Sie die sichtbaren Module und ordnen Sie sie für dieses Hauptbuch an.",
    financialPosition: "Finanzlage",
    financialPositionDescription: "Nettovermögen und die zugehörigen Konten",
    fromPreviousMonth: "gegenüber dem Vormonat",
    ledgerNotes: "Hauptbuchnotizen",
    moneyIn: "Geldeingang",
    moneyMovement: "Geldbewegung",
    moneyMovementDescription:
      "Monatliche Einnahmen und Ausgaben nach Kontokategorie",
    moneyOut: "Geldausgang",
    moveDown: "Nach unten verschieben",
    moveUp: "Nach oben verschieben",
    netWorthDescription: "Aktiva plus Passiva der letzten 12 Monate",
    noMovement: "Keine Geldbewegung in diesem Zeitraum.",
    noRecentActivity: "Keine aktuellen Transaktionen entsprechen den Filtern.",
    partialPeriod: "teilweise",
    precedingPeriodsAverage: "Durchschnitt von bis zu 3 vorherigen Zeiträumen",
    recentActivity: "Letzte Aktivitäten",
    recentActivityCardDescription:
      "Neueste Transaktionen passend zu den Dashboard-Filtern",
    recentActivityDescription: "Buchungsbewusste Aktivitäten aus dem Journal",
    recentActivityError:
      "Die letzten Transaktionen konnten nicht geladen werden.",
    resetLayout: "Layout zurücksetzen",
    showWidget: "Modul anzeigen",
    tableView: "Tabelle anzeigen",
    topSources: "Größte Quellen",
    topSpending: "Größte Ausgaben",
    transactionCleared: "Gebucht",
    transactionMultiple: "Mehrere Buchungen",
    transactionOther: "Sonstige",
    transactionPending: "Ausstehend",
    transactionTransfer: "Umbuchung",
    unknownTransaction: "Transaktion ohne Titel",
    unrealized: "Nicht realisiert {amount}",
    atCostCount_one: "{count} zu Anschaffungskosten (kein Preis)",
    atCostCount_other: "{count} zu Anschaffungskosten (kein Preis)",
    holdingPrice: "Preis {date}",
    livePrice: "Live-Preis",
    noPrice: "Kein Preis",
    notInTotalCount_one: "{count} nicht in der Summe",
    notInTotalCount_other: "{count} nicht in der Summe",
    notInTotalTag: "Nicht in der Summe",
    atCostTag: "Zu Anschaffungskosten",
    priceNotUpdated: "Nicht aktualisiert",
    pricesNotUpdated_one: "{count} Preis seit {date} nicht aktualisiert",
    pricesNotUpdated_other: "{count} Preise seit {date} nicht aktualisiert",
    updatePrices: "Preise aktualisieren",
    valuationDetails: "Bewertungsdetails",
    valuedAtMarket: "Zum Marktwert",
    viewAll: "Alle anzeigen",
    viewAllAccounts: "Alle Konten anzeigen",
  },
  es: {
    accountsDescription: "Saldos actuales de las cuentas de activo y pasivo",
    allWidgetsHidden:
      "Todos los módulos del panel están ocultos. Personaliza el panel para restaurar uno.",
    chartView: "Mostrar gráfico",
    costBasis: "Base de costo {amount}",
    customize: "Personalizar",
    customizeDescription:
      "Elige qué módulos aparecen y ordénalos para este libro.",
    financialPosition: "Posición financiera",
    financialPositionDescription:
      "Patrimonio neto y las cuentas que lo componen",
    fromPreviousMonth: "desde el mes anterior",
    ledgerNotes: "Notas del libro",
    moneyIn: "Entradas de dinero",
    moneyMovement: "Movimiento de dinero",
    moneyMovementDescription:
      "Ingresos y gastos mensuales por categoría de cuenta",
    moneyOut: "Salidas de dinero",
    moveDown: "Mover abajo",
    moveUp: "Mover arriba",
    netWorthDescription: "Activos más pasivos durante los últimos 12 meses",
    noMovement: "No hay movimiento de dinero en este período.",
    noRecentActivity:
      "Ninguna transacción reciente coincide con los filtros actuales.",
    partialPeriod: "parcial",
    precedingPeriodsAverage: "Promedio de hasta 3 períodos anteriores",
    recentActivity: "Actividad reciente",
    recentActivityCardDescription:
      "Transacciones más recientes según los filtros actuales",
    recentActivityDescription: "Actividad del diario con detalle de asientos",
    recentActivityError: "No se pudieron cargar las transacciones recientes.",
    resetLayout: "Restablecer diseño",
    showWidget: "Mostrar módulo",
    tableView: "Mostrar tabla",
    topSources: "Fuentes principales",
    topSpending: "Mayores gastos",
    transactionCleared: "Confirmada",
    transactionMultiple: "Varios asientos",
    transactionOther: "Otra",
    transactionPending: "Pendiente",
    transactionTransfer: "Transferencia",
    unknownTransaction: "Transacción sin título",
    unrealized: "No realizado {amount}",
    atCostCount_one: "{count} al costo (sin precio)",
    atCostCount_many: "{count} al costo (sin precio)",
    atCostCount_other: "{count} al costo (sin precio)",
    holdingPrice: "Precio {date}",
    livePrice: "Precio en vivo",
    noPrice: "Sin precio",
    notInTotalCount_one: "{count} fuera del total",
    notInTotalCount_many: "{count} fuera del total",
    notInTotalCount_other: "{count} fuera del total",
    notInTotalTag: "Fuera del total",
    atCostTag: "Al costo",
    priceNotUpdated: "Sin actualizar",
    pricesNotUpdated_one: "{count} precio sin actualizar desde el {date}",
    pricesNotUpdated_many: "{count} precios sin actualizar desde el {date}",
    pricesNotUpdated_other: "{count} precios sin actualizar desde el {date}",
    updatePrices: "Actualizar precios",
    valuationDetails: "Detalles de la valoración",
    valuedAtMarket: "A valor de mercado",
    viewAll: "Ver todo",
    viewAllAccounts: "Ver todas las cuentas",
  },
  fa: {
    accountsDescription: "مانده فعلی حساب‌های دارایی و بدهی",
    allWidgetsHidden:
      "همه بخش‌های داشبورد پنهان هستند. برای بازگرداندن یک بخش، داشبورد را سفارشی کنید.",
    chartView: "نمایش نمودار",
    costBasis: "بهای تمام شده {amount}",
    customize: "سفارشی‌سازی",
    customizeDescription:
      "بخش‌های قابل نمایش و ترتیب آن‌ها را برای این دفتر انتخاب کنید.",
    financialPosition: "وضعیت مالی",
    financialPositionDescription: "ارزش خالص و حساب‌های تشکیل‌دهنده آن",
    fromPreviousMonth: "نسبت به ماه قبل",
    ledgerNotes: "یادداشت‌های دفتر",
    moneyIn: "ورودی پول",
    moneyMovement: "گردش پول",
    moneyMovementDescription: "درآمد و هزینه ماهانه بر اساس دسته حساب",
    moneyOut: "خروجی پول",
    moveDown: "انتقال به پایین",
    moveUp: "انتقال به بالا",
    netWorthDescription: "دارایی‌ها به‌علاوه بدهی‌ها در ۱۲ ماه گذشته",
    noMovement: "در این دوره گردش پولی وجود ندارد.",
    noRecentActivity: "هیچ تراکنش اخیر با فیلترهای فعلی مطابقت ندارد.",
    partialPeriod: "ناقص",
    precedingPeriodsAverage: "میانگین حداکثر ۳ دوره پیشین",
    recentActivity: "فعالیت اخیر",
    recentActivityCardDescription: "جدیدترین تراکنش‌های مطابق فیلترهای فعلی",
    recentActivityDescription: "فعالیت دفتر روزنامه با درنظرگرفتن ثبت‌ها",
    recentActivityError: "تراکنش‌های اخیر بارگیری نشدند.",
    resetLayout: "بازنشانی چیدمان",
    showWidget: "نمایش بخش",
    tableView: "نمایش جدول",
    topSources: "منابع اصلی",
    topSpending: "بیشترین هزینه‌ها",
    transactionCleared: "تسویه‌شده",
    transactionMultiple: "چند ثبت",
    transactionOther: "سایر",
    transactionPending: "در انتظار",
    transactionTransfer: "انتقال",
    unknownTransaction: "تراکنش بدون عنوان",
    unrealized: "سود و زیان تحقق‌نیافته {amount}",
    atCostCount_one: "{count} به بهای تمام‌شده (بدون قیمت)",
    atCostCount_other: "{count} به بهای تمام‌شده (بدون قیمت)",
    holdingPrice: "قیمت {date}",
    livePrice: "قیمت زنده",
    noPrice: "بدون قیمت",
    notInTotalCount_one: "{count} خارج از جمع کل",
    notInTotalCount_other: "{count} خارج از جمع کل",
    notInTotalTag: "خارج از جمع",
    atCostTag: "به بهای تمام شده",
    priceNotUpdated: "به‌روز نشده",
    pricesNotUpdated_one: "{count} قیمت از {date} به‌روز نشده است",
    pricesNotUpdated_other: "{count} قیمت از {date} به‌روز نشده است",
    updatePrices: "به‌روزرسانی قیمت‌ها",
    valuationDetails: "جزئیات ارزش‌گذاری",
    valuedAtMarket: "به ارزش بازار",
    viewAll: "نمایش همه",
    viewAllAccounts: "نمایش همه حساب‌ها",
  },
  fr: {
    accountsDescription: "Soldes actuels des comptes d'actif et de passif",
    allWidgetsHidden:
      "Tous les modules du tableau de bord sont masqués. Personnalisez-le pour en restaurer un.",
    chartView: "Afficher le graphique",
    costBasis: "Prix de revient {amount}",
    customize: "Personnaliser",
    customizeDescription:
      "Choisissez les modules affichés et organisez-les pour ce grand livre.",
    financialPosition: "Situation financière",
    financialPositionDescription: "Patrimoine net et comptes qui le composent",
    fromPreviousMonth: "depuis le mois précédent",
    ledgerNotes: "Notes du grand livre",
    moneyIn: "Entrées d'argent",
    moneyMovement: "Mouvements d'argent",
    moneyMovementDescription:
      "Revenus et dépenses mensuels par catégorie de compte",
    moneyOut: "Sorties d'argent",
    moveDown: "Descendre",
    moveUp: "Monter",
    netWorthDescription: "Actifs plus passifs sur les 12 derniers mois",
    noMovement: "Aucun mouvement d'argent pour cette période.",
    noRecentActivity:
      "Aucune transaction récente ne correspond aux filtres actuels.",
    partialPeriod: "partiel",
    precedingPeriodsAverage: "Moyenne sur 3 périodes précédentes au maximum",
    recentActivity: "Activité récente",
    recentActivityCardDescription:
      "Transactions les plus récentes selon les filtres actuels",
    recentActivityDescription:
      "Activité du journal tenant compte des écritures",
    recentActivityError: "Impossible de charger les transactions récentes.",
    resetLayout: "Réinitialiser la disposition",
    showWidget: "Afficher le module",
    tableView: "Afficher le tableau",
    topSources: "Principales sources",
    topSpending: "Principales dépenses",
    transactionCleared: "Validée",
    transactionMultiple: "Plusieurs écritures",
    transactionOther: "Autre",
    transactionPending: "En attente",
    transactionTransfer: "Virement",
    unknownTransaction: "Transaction sans titre",
    unrealized: "Résultat latent {amount}",
    atCostCount_one: "{count} au coût (sans prix)",
    atCostCount_many: "{count} au coût (sans prix)",
    atCostCount_other: "{count} au coût (sans prix)",
    holdingPrice: "Prix {date}",
    livePrice: "Prix en direct",
    noPrice: "Sans prix",
    notInTotalCount_one: "{count} hors total",
    notInTotalCount_many: "{count} hors total",
    notInTotalCount_other: "{count} hors total",
    notInTotalTag: "Hors total",
    atCostTag: "Au coût",
    priceNotUpdated: "Non mis à jour",
    pricesNotUpdated_one: "{count} prix non mis à jour depuis le {date}",
    pricesNotUpdated_many: "{count} prix non mis à jour depuis le {date}",
    pricesNotUpdated_other: "{count} prix non mis à jour depuis le {date}",
    updatePrices: "Mettre à jour les prix",
    valuationDetails: "Détails de la valorisation",
    valuedAtMarket: "À la valeur de marché",
    viewAll: "Tout afficher",
    viewAllAccounts: "Afficher tous les comptes",
  },
  ja: {
    accountsDescription: "資産・負債勘定の現在残高",
    allWidgetsHidden:
      "すべてのダッシュボードモジュールが非表示です。カスタマイズして復元してください。",
    chartView: "チャートを表示",
    costBasis: "取得原価 {amount}",
    customize: "カスタマイズ",
    customizeDescription: "この元帳に表示するモジュールと順序を選択します。",
    financialPosition: "財務状況",
    financialPositionDescription: "純資産とその内訳となる勘定",
    fromPreviousMonth: "前月比",
    ledgerNotes: "元帳ノート",
    moneyIn: "入金",
    moneyMovement: "資金移動",
    moneyMovementDescription: "勘定カテゴリ別の月次収入と支出",
    moneyOut: "出金",
    moveDown: "下へ移動",
    moveUp: "上へ移動",
    netWorthDescription: "過去12か月の資産と負債の合計",
    noMovement: "この期間の資金移動はありません。",
    noRecentActivity: "現在のフィルターに一致する最近の取引はありません。",
    partialPeriod: "途中",
    precedingPeriodsAverage: "直近最大3期間の平均",
    recentActivity: "最近のアクティビティ",
    recentActivityCardDescription: "現在のフィルターに一致する最新の取引",
    recentActivityDescription: "仕訳を考慮した元帳のアクティビティ",
    recentActivityError: "最近の取引を読み込めませんでした。",
    resetLayout: "レイアウトをリセット",
    showWidget: "モジュールを表示",
    tableView: "表を表示",
    topSources: "主な収入源",
    topSpending: "主な支出",
    transactionCleared: "確定済み",
    transactionMultiple: "複数の仕訳",
    transactionOther: "その他",
    transactionPending: "保留中",
    transactionTransfer: "振替",
    unknownTransaction: "無題の取引",
    unrealized: "含み損益 {amount}",
    atCostCount_other: "取得原価（価格なし）{count} 件",
    holdingPrice: "価格 {date}",
    livePrice: "ライブ価格",
    noPrice: "価格なし",
    notInTotalCount_other: "合計対象外 {count} 件",
    notInTotalTag: "合計に含まれない",
    atCostTag: "取得原価",
    priceNotUpdated: "未更新",
    pricesNotUpdated_other: "{date} 以降、{count} 件の価格が未更新",
    updatePrices: "価格を更新",
    valuationDetails: "評価の詳細",
    valuedAtMarket: "市場価値で評価",
    viewAll: "すべて表示",
    viewAllAccounts: "すべての勘定を表示",
  },
  ko: {
    accountsDescription: "현재 자산 및 부채 계정 잔액",
    allWidgetsHidden:
      "모든 대시보드 모듈이 숨겨져 있습니다. 대시보드를 사용자 지정해 복원하세요.",
    chartView: "차트 보기",
    costBasis: "취득 원가 {amount}",
    customize: "사용자 지정",
    customizeDescription: "이 원장에 표시할 모듈과 순서를 선택하세요.",
    financialPosition: "재무 상태",
    financialPositionDescription: "순자산과 이를 구성하는 계정",
    fromPreviousMonth: "전월 대비",
    ledgerNotes: "원장 메모",
    moneyIn: "유입",
    moneyMovement: "자금 흐름",
    moneyMovementDescription: "계정 범주별 월간 수입과 지출",
    moneyOut: "유출",
    moveDown: "아래로 이동",
    moveUp: "위로 이동",
    netWorthDescription: "지난 12개월의 자산과 부채 합계",
    noMovement: "이 기간에는 자금 이동이 없습니다.",
    noRecentActivity: "현재 필터와 일치하는 최근 거래가 없습니다.",
    partialPeriod: "진행 중",
    precedingPeriodsAverage: "이전 최대 3개 기간의 평균",
    recentActivity: "최근 활동",
    recentActivityCardDescription: "현재 필터와 일치하는 최신 거래",
    recentActivityDescription: "분개를 반영한 원장 활동",
    recentActivityError: "최근 거래를 불러오지 못했습니다.",
    resetLayout: "레이아웃 초기화",
    showWidget: "모듈 표시",
    tableView: "표 보기",
    topSources: "주요 수입원",
    topSpending: "주요 지출",
    transactionCleared: "완료",
    transactionMultiple: "여러 분개",
    transactionOther: "기타",
    transactionPending: "대기 중",
    transactionTransfer: "이체",
    unknownTransaction: "제목 없는 거래",
    unrealized: "평가 손익 {amount}",
    atCostCount_other: "원가 기준(가격 없음) {count}개",
    holdingPrice: "가격 {date}",
    livePrice: "실시간 가격",
    noPrice: "가격 없음",
    notInTotalCount_other: "합계 제외 {count}개",
    notInTotalTag: "합계에서 제외",
    atCostTag: "원가 기준",
    priceNotUpdated: "업데이트 안 됨",
    pricesNotUpdated_other: "{date} 이후 가격 {count}개 미갱신",
    updatePrices: "가격 업데이트",
    valuationDetails: "평가 세부 정보",
    valuedAtMarket: "시장 가치 기준",
    viewAll: "모두 보기",
    viewAllAccounts: "모든 계정 보기",
  },
  nl: {
    accountsDescription: "Huidige saldi van activa- en passivarekeningen",
    allWidgetsHidden:
      "Alle dashboardmodules zijn verborgen. Pas het dashboard aan om er een te herstellen.",
    chartView: "Grafiek tonen",
    costBasis: "Kostprijs {amount}",
    customize: "Aanpassen",
    customizeDescription:
      "Kies welke modules verschijnen en rangschik ze voor dit grootboek.",
    financialPosition: "Financiële positie",
    financialPositionDescription: "Nettovermogen en de bijbehorende rekeningen",
    fromPreviousMonth: "ten opzichte van vorige maand",
    ledgerNotes: "Grootboeknotities",
    moneyIn: "Geld in",
    moneyMovement: "Geldbeweging",
    moneyMovementDescription:
      "Maandelijkse inkomsten en uitgaven per rekeningcategorie",
    moneyOut: "Geld uit",
    moveDown: "Omlaag verplaatsen",
    moveUp: "Omhoog verplaatsen",
    netWorthDescription: "Activa plus passiva over de afgelopen 12 maanden",
    noMovement: "Geen geldbeweging in deze periode.",
    noRecentActivity: "Geen recente transacties passen bij de huidige filters.",
    partialPeriod: "gedeeltelijk",
    precedingPeriodsAverage: "Gemiddelde van maximaal 3 voorgaande perioden",
    recentActivity: "Recente activiteit",
    recentActivityCardDescription:
      "Nieuwste transacties volgens de huidige filters",
    recentActivityDescription: "Journaalactiviteit met aandacht voor boekingen",
    recentActivityError: "Recente transacties konden niet worden geladen.",
    resetLayout: "Indeling herstellen",
    showWidget: "Module tonen",
    tableView: "Tabel tonen",
    topSources: "Belangrijkste bronnen",
    topSpending: "Grootste uitgaven",
    transactionCleared: "Verwerkt",
    transactionMultiple: "Meerdere boekingen",
    transactionOther: "Overig",
    transactionPending: "In behandeling",
    transactionTransfer: "Overboeking",
    unknownTransaction: "Transactie zonder titel",
    unrealized: "Ongerealiseerd {amount}",
    atCostCount_one: "{count} tegen kostprijs (geen prijs)",
    atCostCount_other: "{count} tegen kostprijs (geen prijs)",
    holdingPrice: "Prijs {date}",
    livePrice: "Live prijs",
    noPrice: "Geen prijs",
    notInTotalCount_one: "{count} niet in totaal",
    notInTotalCount_other: "{count} niet in totaal",
    notInTotalTag: "Niet in totaal",
    atCostTag: "Tegen kostprijs",
    priceNotUpdated: "Niet bijgewerkt",
    pricesNotUpdated_one: "{count} prijs niet bijgewerkt sinds {date}",
    pricesNotUpdated_other: "{count} prijzen niet bijgewerkt sinds {date}",
    updatePrices: "Prijzen bijwerken",
    valuationDetails: "Waarderingsdetails",
    valuedAtMarket: "Tegen marktwaarde",
    viewAll: "Alles bekijken",
    viewAllAccounts: "Alle rekeningen bekijken",
  },
  pt: {
    accountsDescription: "Saldos atuais das contas de ativos e passivos",
    allWidgetsHidden:
      "Todos os módulos do painel estão ocultos. Personalize o painel para restaurar um.",
    chartView: "Mostrar gráfico",
    costBasis: "Custo de aquisição {amount}",
    customize: "Personalizar",
    customizeDescription:
      "Escolha os módulos exibidos e organize-os para este livro-razão.",
    financialPosition: "Posição financeira",
    financialPositionDescription:
      "Patrimônio líquido e as contas que o compõem",
    fromPreviousMonth: "desde o mês anterior",
    ledgerNotes: "Notas do livro-razão",
    moneyIn: "Entradas",
    moneyMovement: "Movimentação de dinheiro",
    moneyMovementDescription:
      "Receitas e despesas mensais por categoria de conta",
    moneyOut: "Saídas",
    moveDown: "Mover para baixo",
    moveUp: "Mover para cima",
    netWorthDescription: "Ativos mais passivos nos últimos 12 meses",
    noMovement: "Não houve movimentação de dinheiro neste período.",
    noRecentActivity:
      "Nenhuma transação recente corresponde aos filtros atuais.",
    partialPeriod: "parcial",
    precedingPeriodsAverage: "Média de até 3 períodos anteriores",
    recentActivity: "Atividade recente",
    recentActivityCardDescription:
      "Transações mais recentes segundo os filtros atuais",
    recentActivityDescription:
      "Atividade do diário considerando os lançamentos",
    recentActivityError: "Não foi possível carregar as transações recentes.",
    resetLayout: "Redefinir layout",
    showWidget: "Mostrar módulo",
    tableView: "Mostrar tabela",
    topSources: "Principais fontes",
    topSpending: "Maiores despesas",
    transactionCleared: "Compensada",
    transactionMultiple: "Vários lançamentos",
    transactionOther: "Outra",
    transactionPending: "Pendente",
    transactionTransfer: "Transferência",
    unknownTransaction: "Transação sem título",
    unrealized: "Não realizado {amount}",
    atCostCount_one: "{count} ao custo (sem preço)",
    atCostCount_many: "{count} ao custo (sem preço)",
    atCostCount_other: "{count} ao custo (sem preço)",
    holdingPrice: "Preço {date}",
    livePrice: "Preço ao vivo",
    noPrice: "Sem preço",
    notInTotalCount_one: "{count} fora do total",
    notInTotalCount_many: "{count} fora do total",
    notInTotalCount_other: "{count} fora do total",
    notInTotalTag: "Fora do total",
    atCostTag: "Ao custo",
    priceNotUpdated: "Não atualizado",
    pricesNotUpdated_one: "{count} preço sem atualização desde {date}",
    pricesNotUpdated_many: "{count} preços sem atualização desde {date}",
    pricesNotUpdated_other: "{count} preços sem atualização desde {date}",
    updatePrices: "Atualizar preços",
    valuationDetails: "Detalhes da avaliação",
    valuedAtMarket: "Ao valor de mercado",
    viewAll: "Ver tudo",
    viewAllAccounts: "Ver todas as contas",
  },
  ru: {
    accountsDescription: "Текущие остатки по счетам активов и обязательств",
    allWidgetsHidden:
      "Все модули панели скрыты. Настройте панель, чтобы вернуть нужный модуль.",
    chartView: "Показать график",
    costBasis: "Себестоимость {amount}",
    customize: "Настроить",
    customizeDescription:
      "Выберите отображаемые модули и расположите их для этой книги.",
    financialPosition: "Финансовое положение",
    financialPositionDescription: "Чистая стоимость и составляющие её счета",
    fromPreviousMonth: "по сравнению с прошлым месяцем",
    ledgerNotes: "Заметки книги",
    moneyIn: "Поступления",
    moneyMovement: "Движение денег",
    moneyMovementDescription:
      "Ежемесячные доходы и расходы по категориям счетов",
    moneyOut: "Списания",
    moveDown: "Переместить вниз",
    moveUp: "Переместить вверх",
    netWorthDescription: "Активы плюс обязательства за последние 12 месяцев",
    noMovement: "В этом периоде нет движения денег.",
    noRecentActivity:
      "Нет недавних транзакций, соответствующих текущим фильтрам.",
    partialPeriod: "неполный",
    precedingPeriodsAverage: "Среднее не более чем за 3 предыдущих периода",
    recentActivity: "Последние операции",
    recentActivityCardDescription: "Новые транзакции по текущим фильтрам",
    recentActivityDescription: "Операции журнала с учётом проводок",
    recentActivityError: "Не удалось загрузить последние транзакции.",
    resetLayout: "Сбросить макет",
    showWidget: "Показать модуль",
    tableView: "Показать таблицу",
    topSources: "Основные источники",
    topSpending: "Основные расходы",
    transactionCleared: "Проведена",
    transactionMultiple: "Несколько проводок",
    transactionOther: "Другая",
    transactionPending: "Ожидает",
    transactionTransfer: "Перевод",
    unknownTransaction: "Транзакция без названия",
    unrealized: "Нереализованный результат {amount}",
    atCostCount_one: "{count} по себестоимости (нет цены)",
    atCostCount_few: "{count} по себестоимости (нет цены)",
    atCostCount_many: "{count} по себестоимости (нет цены)",
    atCostCount_other: "{count} по себестоимости (нет цены)",
    holdingPrice: "Цена {date}",
    livePrice: "Живая цена",
    noPrice: "Нет цены",
    notInTotalCount_one: "{count} вне итога",
    notInTotalCount_few: "{count} вне итога",
    notInTotalCount_many: "{count} вне итога",
    notInTotalCount_other: "{count} вне итога",
    notInTotalTag: "Не в итоге",
    atCostTag: "По себестоимости",
    priceNotUpdated: "Не обновлена",
    pricesNotUpdated_one: "{count} цена не обновлялась с {date}",
    pricesNotUpdated_few: "{count} цены не обновлялись с {date}",
    pricesNotUpdated_many: "{count} цен не обновлялись с {date}",
    pricesNotUpdated_other: "{count} цены не обновлялись с {date}",
    updatePrices: "Обновить цены",
    valuationDetails: "Детали оценки",
    valuedAtMarket: "По рыночной стоимости",
    viewAll: "Показать все",
    viewAllAccounts: "Показать все счета",
  },
  sk: {
    accountsDescription: "Aktuálne zostatky účtov aktív a pasív",
    allWidgetsHidden:
      "Všetky moduly nástenky sú skryté. Prispôsobte nástenku a niektorý obnovte.",
    chartView: "Zobraziť graf",
    costBasis: "Obstarávacia cena {amount}",
    customize: "Prispôsobiť",
    customizeDescription:
      "Vyberte zobrazené moduly a usporiadajte ich pre túto účtovnú knihu.",
    financialPosition: "Finančná pozícia",
    financialPositionDescription: "Čistá hodnota a účty, ktoré ju tvoria",
    fromPreviousMonth: "oproti predchádzajúcemu mesiacu",
    ledgerNotes: "Poznámky ku knihe",
    moneyIn: "Príjmy",
    moneyMovement: "Pohyb peňazí",
    moneyMovementDescription: "Mesačné príjmy a výdavky podľa kategórie účtu",
    moneyOut: "Výdavky",
    moveDown: "Posunúť nadol",
    moveUp: "Posunúť nahor",
    netWorthDescription: "Aktíva plus pasíva za posledných 12 mesiacov",
    noMovement: "V tomto období nebol žiadny pohyb peňazí.",
    noRecentActivity:
      "Aktuálnym filtrom nezodpovedajú žiadne nedávne transakcie.",
    partialPeriod: "čiastočné",
    precedingPeriodsAverage: "Priemer najviac 3 predchádzajúcich období",
    recentActivity: "Nedávna aktivita",
    recentActivityCardDescription:
      "Najnovšie transakcie podľa aktuálnych filtrov",
    recentActivityDescription: "Aktivita denníka so zohľadnením zápisov",
    recentActivityError: "Nedávne transakcie sa nepodarilo načítať.",
    resetLayout: "Obnoviť rozloženie",
    showWidget: "Zobraziť modul",
    tableView: "Zobraziť tabuľku",
    topSources: "Hlavné zdroje",
    topSpending: "Najväčšie výdavky",
    transactionCleared: "Zaúčtovaná",
    transactionMultiple: "Viacero zápisov",
    transactionOther: "Iná",
    transactionPending: "Čakajúca",
    transactionTransfer: "Prevod",
    unknownTransaction: "Transakcia bez názvu",
    unrealized: "Nerealizovaný výsledok {amount}",
    atCostCount_one: "{count} v obstarávacej cene (bez ceny)",
    atCostCount_few: "{count} v obstarávacej cene (bez ceny)",
    atCostCount_many: "{count} v obstarávacej cene (bez ceny)",
    atCostCount_other: "{count} v obstarávacej cene (bez ceny)",
    holdingPrice: "Cena {date}",
    livePrice: "Živá cena",
    noPrice: "Bez ceny",
    notInTotalCount_one: "{count} mimo súčtu",
    notInTotalCount_few: "{count} mimo súčtu",
    notInTotalCount_many: "{count} mimo súčtu",
    notInTotalCount_other: "{count} mimo súčtu",
    notInTotalTag: "Mimo súčtu",
    atCostTag: "V obstarávacej cene",
    priceNotUpdated: "Neaktualizovaná",
    pricesNotUpdated_one: "{count} cena neaktualizovaná od {date}",
    pricesNotUpdated_few: "{count} ceny neaktualizované od {date}",
    pricesNotUpdated_many: "{count} ceny neaktualizované od {date}",
    pricesNotUpdated_other: "{count} cien neaktualizovaných od {date}",
    updatePrices: "Aktualizovať ceny",
    valuationDetails: "Podrobnosti ocenenia",
    valuedAtMarket: "V trhovej hodnote",
    viewAll: "Zobraziť všetko",
    viewAllAccounts: "Zobraziť všetky účty",
  },
  uk: {
    accountsDescription: "Поточні залишки на рахунках активів і зобов'язань",
    allWidgetsHidden:
      "Усі модулі панелі приховано. Налаштуйте панель, щоб відновити модуль.",
    chartView: "Показати графік",
    costBasis: "Собівартість {amount}",
    customize: "Налаштувати",
    customizeDescription:
      "Виберіть модулі для показу та впорядкуйте їх для цієї книги.",
    financialPosition: "Фінансовий стан",
    financialPositionDescription: "Чиста вартість і рахунки, що її формують",
    fromPreviousMonth: "порівняно з попереднім місяцем",
    ledgerNotes: "Нотатки книги",
    moneyIn: "Надходження",
    moneyMovement: "Рух коштів",
    moneyMovementDescription:
      "Щомісячні доходи й витрати за категоріями рахунків",
    moneyOut: "Витрати",
    moveDown: "Перемістити вниз",
    moveUp: "Перемістити вгору",
    netWorthDescription: "Активи плюс зобов'язання за останні 12 місяців",
    noMovement: "У цьому періоді руху коштів немає.",
    noRecentActivity: "Немає недавніх транзакцій за поточними фільтрами.",
    partialPeriod: "неповний",
    precedingPeriodsAverage: "Середнє щонайбільше за 3 попередні періоди",
    recentActivity: "Остання активність",
    recentActivityCardDescription:
      "Найновіші транзакції за поточними фільтрами",
    recentActivityDescription: "Активність журналу з урахуванням проведень",
    recentActivityError: "Не вдалося завантажити останні транзакції.",
    resetLayout: "Скинути розкладку",
    showWidget: "Показати модуль",
    tableView: "Показати таблицю",
    topSources: "Основні джерела",
    topSpending: "Найбільші витрати",
    transactionCleared: "Проведена",
    transactionMultiple: "Кілька проведень",
    transactionOther: "Інша",
    transactionPending: "Очікує",
    transactionTransfer: "Переказ",
    unknownTransaction: "Транзакція без назви",
    unrealized: "Нереалізований результат {amount}",
    atCostCount_one: "{count} за собівартістю (без ціни)",
    atCostCount_few: "{count} за собівартістю (без ціни)",
    atCostCount_many: "{count} за собівартістю (без ціни)",
    atCostCount_other: "{count} за собівартістю (без ціни)",
    holdingPrice: "Ціна {date}",
    livePrice: "Жива ціна",
    noPrice: "Немає ціни",
    notInTotalCount_one: "{count} поза підсумком",
    notInTotalCount_few: "{count} поза підсумком",
    notInTotalCount_many: "{count} поза підсумком",
    notInTotalCount_other: "{count} поза підсумком",
    notInTotalTag: "Не в підсумку",
    atCostTag: "За собівартістю",
    priceNotUpdated: "Не оновлено",
    pricesNotUpdated_one: "{count} ціна не оновлювалася з {date}",
    pricesNotUpdated_few: "{count} ціни не оновлювалися з {date}",
    pricesNotUpdated_many: "{count} цін не оновлювалися з {date}",
    pricesNotUpdated_other: "{count} ціни не оновлювалися з {date}",
    updatePrices: "Оновити ціни",
    valuationDetails: "Деталі оцінки",
    valuedAtMarket: "За ринковою вартістю",
    viewAll: "Показати все",
    viewAllAccounts: "Показати всі рахунки",
  },
  zh: {
    accountsDescription: "当前资产和负债账户余额",
    allWidgetsHidden: "所有仪表板模块均已隐藏。请自定义仪表板以恢复模块。",
    chartView: "显示图表",
    costBasis: "成本 {amount}",
    customize: "自定义",
    customizeDescription: "选择要显示的模块，并为此账本调整顺序。",
    financialPosition: "财务状况",
    financialPositionDescription: "净资产及其组成账户",
    fromPreviousMonth: "较上月",
    ledgerNotes: "账本备注",
    moneyIn: "资金流入",
    moneyMovement: "资金流动",
    moneyMovementDescription: "按账户类别显示每月收入和支出",
    moneyOut: "资金流出",
    moveDown: "下移",
    moveUp: "上移",
    netWorthDescription: "过去 12 个月的资产与负债之和",
    noMovement: "此期间没有资金流动。",
    noRecentActivity: "没有符合当前筛选条件的近期交易。",
    partialPeriod: "未完整",
    precedingPeriodsAverage: "最多前 3 个期间的平均值",
    recentActivity: "近期活动",
    recentActivityCardDescription: "符合当前筛选条件的最新交易",
    recentActivityDescription: "考虑分录的账本活动",
    recentActivityError: "无法加载近期交易。",
    resetLayout: "重置布局",
    showWidget: "显示模块",
    tableView: "显示表格",
    topSources: "主要来源",
    topSpending: "主要支出",
    transactionCleared: "已入账",
    transactionMultiple: "多笔分录",
    transactionOther: "其他",
    transactionPending: "待处理",
    transactionTransfer: "转账",
    unknownTransaction: "未命名交易",
    unrealized: "浮动盈亏 {amount}",
    atCostCount_other: "{count} 项按成本（无价格）",
    holdingPrice: "价格 {date}",
    livePrice: "实时价格",
    noPrice: "无价格",
    notInTotalCount_other: "{count} 项未计入合计",
    notInTotalTag: "未计入合计",
    atCostTag: "按成本",
    priceNotUpdated: "未更新",
    pricesNotUpdated_other: "{count} 个价格自 {date} 起未更新",
    updatePrices: "更新价格",
    valuationDetails: "估值明细",
    valuedAtMarket: "按市值",
    viewAll: "查看全部",
    viewAllAccounts: "查看所有账户",
  },
};

function buildDashboardTranslations(
  language: DashboardLanguage,
): Record<string, DashboardTranslationEntry> {
  return Object.fromEntries(
    Object.entries(messages[language] as Record<string, string>).map(
      ([key, message]) => [
        `page.overview.${key}`,
        {
          message,
          description:
            descriptions[
              key.replace(PLURAL_SUFFIX, "") as DashboardTranslationKey
            ],
        },
      ],
    ),
  );
}

export const dashboardOverviewTranslations: Record<
  DashboardLanguage,
  Record<string, DashboardTranslationEntry>
> = {
  bg: buildDashboardTranslations("bg"),
  ca: buildDashboardTranslations("ca"),
  de: buildDashboardTranslations("de"),
  en: buildDashboardTranslations("en"),
  es: buildDashboardTranslations("es"),
  fa: buildDashboardTranslations("fa"),
  fr: buildDashboardTranslations("fr"),
  ja: buildDashboardTranslations("ja"),
  ko: buildDashboardTranslations("ko"),
  nl: buildDashboardTranslations("nl"),
  pt: buildDashboardTranslations("pt"),
  ru: buildDashboardTranslations("ru"),
  sk: buildDashboardTranslations("sk"),
  uk: buildDashboardTranslations("uk"),
  zh: buildDashboardTranslations("zh"),
};
